<script setup>
import { ref, onMounted, reactive, computed, watch } from 'vue'
import { gApp } from '../Globals.js'

const props = defineProps({
  absPath: {
    type: String,
    default: '',
  },
  relPath: {
    type: String,
    default: '',
  },
});

let path = computed(() => {
  let basePath = gApp.proj().getBaseRoute();
  if (props.absPath) {
    return props.absPath;
  } else if (props.relPath) {
    return `${basePath}/${props.relPath}`;
  } else {
    return basePath;
  }
});

let errorClasses = computed(() => {
  let projectErrors = gApp.proj().getProjectErrors();
  if (!props.relPath) {
    return {};
  }
  return {
    'PageHasErrors': projectErrors.checkPageHasErrors(props.relPath),
  };
});

let pageHighlighted = computed(() => {
  return gApp.proj().isPagePathHighlighted(props.relPath);
});

</script>

<template>
  <router-link :to="{path: path}" class="MenuLink Block" :class="errorClasses">
    <div v-if="pageHighlighted" class="PageHighlighted">
      <span class="HighlightArrow">→</span>
    </div>
    <slot></slot>
  </router-link>
</template>

<style scoped>
.MenuLink {
  font-weight: bold;
  font-size: var(--f-s);
  color: var(--pc-lightest);
  width: 100%;
  padding: var(--xs) var(--s) var(--xs) var(--m);
  margin: 2px 0;
  border-radius: 0 8px 8px 0;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  display: flex;
  align-items: center;
}

.MenuLink::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 3px;
  background: linear-gradient(to bottom, #4e7cff, #6a5acd);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.MenuLink:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  transform: translateX(5px);
  padding-left: calc(var(--m) + 5px);
}

.MenuLink:hover::before {
  opacity: 1;
}

.MenuLink i {
  margin-right: var(--space-xs);
  font-size: 1.1em;
  transition: transform 0.2s ease;
}

.MenuLink:hover i {
  transform: scale(1.2);
}

.router-link-exact-active {
  background: linear-gradient(to right, var(--pc), rgba(62, 85, 204, 0.7));
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.router-link-exact-active::before {
  opacity: 1;
}

.router-link-exact-active i {
  color: white;
}

.PageHasErrors {
  position: relative;
  overflow: hidden;
}

.PageHighlighted {
  display: flex;
  align-items: center;
}

.HighlightArrow {
  color: rgba(255, 193, 7, 1);
  font-size: 20px;
  font-weight: bold;
  text-shadow: 0 0 8px rgba(255, 193, 7, 0.8);
  margin-right: 8px;
  animation: moveArrow 1.5s infinite;
}

@keyframes moveArrow {
  0% { transform: translateX(-3px); opacity: 0.8; }
  50% { transform: translateX(3px); opacity: 1; }
  100% { transform: translateX(-3px); opacity: 0.8; }
}

.PageHasErrors::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  background-color: var(--delete-red);
  animation: pulseErrorBorder 2s infinite;
}

.PageHasErrors::after {
  content: '!';
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  background-color: var(--delete-red);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  animation: pulseError 2s infinite;
  box-shadow: 0 0 8px rgba(231, 4, 15, 0.5);
}

@keyframes pulseErrorBorder {
  0% { opacity: 0.7; }
  50% { opacity: 1; }
  100% { opacity: 0.7; }
}

@keyframes pulseError {
  0% { transform: translateY(-50%) scale(1); opacity: 0.9; }
  50% { transform: translateY(-50%) scale(1.15); opacity: 1; }
  100% { transform: translateY(-50%) scale(1); opacity: 0.9; }
}
</style>
